import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  ASSISTANT_HELP_CENTER_ORIGINS,
  ASSISTANT_HELP_CENTER_SEARCH_PATH,
  type AssistantHelpArticlesOutput,
} from '../contract'

const ALLOWED_ORIGINS = new Set<string>(ASSISTANT_HELP_CENTER_ORIGINS)

/**
 * The link, only when it is https on the help center's own host. Results
 * come back from the browser's history, so anything else (another host,
 * `javascript:`) is never rendered as a link.
 */
const safeHelpCenterUrl = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null
  try {
    const url = new URL(raw)
    return url.protocol === 'https:' && ALLOWED_ORIGINS.has(url.origin)
      ? url.href
      : null
  } catch {
    return null
  }
}

/** The help center's own search page for the query. */
const helpCenterSearchUrl = (query: string) =>
  `${
    ASSISTANT_HELP_CENTER_ORIGINS[0]
  }${ASSISTANT_HELP_CENTER_SEARCH_PATH}?term=${encodeURIComponent(
    query.trim()
  )}`

/** "Updated Mar 2026", or nothing for a malformed date. */
const formatUpdated = (updated: unknown): string | null => {
  if (typeof updated !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(updated)) {
    return null
  }
  const date = new Date(`${updated}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return null
  return `Updated ${date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })}`
}

const linkSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  p: 1.25,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  '&:hover': { borderColor: 'primary.main' },
} as const

const ExternalLink = ({
  href,
  label,
  children,
}: {
  href: string
  label: string
  children: ReactNode
}) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    underline="none"
    aria-label={`${label} (opens in a new tab)`}
    data-testid="assistant-help-link"
    sx={linkSx}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    <OpenInNewIcon fontSize="small" color="action" aria-hidden />
  </Link>
)

/**
 * Help-center articles the answer came from, as links. The model's reply
 * already summarizes them, so the excerpts aren't shown.
 */
const HelpArticlesCard = ({
  output,
}: {
  output: AssistantHelpArticlesOutput
}) => {
  if (output.limited) return null

  // A long section is split into several chunks with the same heading; the
  // model gets each, the user gets one link.
  const seen = new Set<string>()
  const articles = (Array.isArray(output.articles) ? output.articles : [])
    .flatMap(article => {
      const href = safeHelpCenterUrl(article?.url)
      if (!href || typeof article.title !== 'string') return []
      const key = `${href}#${article.section ?? ''}`
      if (seen.has(key)) return []
      seen.add(key)
      return [{ ...article, href, key }]
    })
    .slice(0, 3)

  if (articles.length === 0) {
    const query = typeof output.query === 'string' ? output.query : ''
    return (
      <Box data-testid="assistant-help-articles">
        <ExternalLink
          href={helpCenterSearchUrl(query)}
          label="Search the help center"
        >
          <Typography variant="body2" fontWeight={700}>
            Search the help center
          </Typography>
        </ExternalLink>
      </Box>
    )
  }

  return (
    <Box
      data-testid="assistant-help-articles"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Typography variant="overline" color="text.secondary">
        From the help center
      </Typography>
      {articles.map(article => {
        const name = article.section
          ? `${article.title}: ${article.section}`
          : article.title
        const details = [article.category, formatUpdated(article.updated)]
          .filter(Boolean)
          .join(' · ')
        return (
          <ExternalLink key={article.key} href={article.href} label={name}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ overflowWrap: 'anywhere' }}
            >
              {article.title}
            </Typography>
            {article.section && (
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {article.section}
              </Typography>
            )}
            {details && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
              >
                {details}
              </Typography>
            )}
          </ExternalLink>
        )
      })}
    </Box>
  )
}

export default HelpArticlesCard
